/**
 * Dependency imports
 */
import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Box, BoxProps, Text } from '@mantine/core';

/**
 * Local imports
 */

/**
 * Root
 */
const Root = styled(Box, {
  shouldForwardProp: (prop: PropertyKey) =>
    !['cycle'].includes(prop.toString()),
})<Partial<CyclePickerCombinedProps> & { cycle: 'yearly' | 'monthly' }>(
  ({ cycle }) => css`
    display: inline-flex;
    padding: 2px;
    background: linear-gradient(
      90deg,
      var(--color-secondary) 0%,
      var(--color-primary) 100%
    );
    border-radius: 999px;

    > div {
      position: relative;
      width: 21rem;
      height: 3.2rem;
      background: #fff;
      border-radius: inherit;

      > div {
        position: absolute;
        top: 3px;
        bottom: 3px;
        right: 3px;
        left: 3px;

        > p {
          margin: 0;
          text-align: center;
          line-height: calc(3.2rem - 6px);
        }

        > button {
          width: 50%;
          margin: 0;
          line-height: calc(3.2rem - 6px);
          cursor: pointer;
          border: none;
          background: none;
          border-radius: inherit;
        }
      }

      > div:nth-of-type(1) {
        overflow: hidden;
        pointer-events: none;
        z-index: 1;
        right: 50%;
        color: #fff;
        background: #222;
        border-radius: inherit;
        transition: right 240ms cubic-bezier(0.75, 0.32, 0.28, 0.76),
          left 240ms cubic-bezier(0.75, 0.32, 0.28, 0.76);

        > p {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          color: #fff;
          transform: translateX(${cycle === 'monthly' ? '-100%' : '0'});
          transition: transform 240ms cubic-bezier(0.75, 0.32, 0.28, 0.76);

          &:nth-of-type(2) {
            left: 100%;
          }
        }

        ${cycle === 'monthly' &&
        css`
          right: 3px;
          left: 50%;
        `}
      }
    }
  `
);

/**
 * CyclePicker Component
 */
export interface CyclePickerProps {
  onChange?: (cycle: 'yearly' | 'monthly') => void;
  children?: React.ReactNode;
}

export type CyclePickerCombinedProps = CyclePickerProps & BoxProps;

const CyclePicker: React.FC<CyclePickerCombinedProps> = ({
  onChange,
  ...props
}) => {
  const [cycle, setCycle] = React.useState<'yearly' | 'monthly'>('yearly');

  React.useEffect(() => {
    if (onChange) {
      onChange(cycle);
    }
  }, [cycle, onChange]);

  return (
    <Root {...props} cycle={cycle}>
      <div>
        <div>
          <Text>Yearly</Text>
          <Text>Monthly</Text>
        </div>

        <div>
          <button onClick={() => setCycle('yearly')}>Yearly</button>
          <button onClick={() => setCycle('monthly')}>Monthly</button>
        </div>
      </div>
    </Root>
  );
};

export default CyclePicker;
